import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import clsx from 'clsx';
import get from 'lodash/get';
import find from 'lodash/find';
import differenceBy from 'lodash/differenceBy';
import remove from 'lodash/remove';
import every from 'lodash/every';
import unionBy from 'lodash/unionBy';
import { FontIcon } from 'office-ui-fabric-react/lib/Icon';
import { Dropdown } from 'office-ui-fabric-react/lib/Dropdown';
import { Text } from 'office-ui-fabric-react/lib/Text';
import { IconButton, PrimaryButton } from 'office-ui-fabric-react';
import { Link, useHistory, useLocation } from 'react-router-dom';
import Module from '../../components/Module';
import ModuleContentList from '../../components/ModuleContentList';
import SearchBar from '../../components/SearchBar';
import { fetchProducts } from '../../actions/products';
import { fetchPartners } from '../../actions/partners';
import { search } from '../../actions/search';
import PageTitle from '../../components/PageTitle';
import Main from '../../components/Main';
import styles from './BundleProductList.style';
import theme from '../../theme.style';
import TextButton from '../../components/TextButton';
import ProductsList from '../../components/ProductsList';

const BundleProductList = () => {
  const dispatch = useDispatch();
  const { bundleFormData, bundleId, isEdit } = useLocation();
  const [sortAscending, setSortAscending] = useState(false);
  const [sortColumn, setSortColumn] = useState();
  const [loading, setLoading] = useState(true);
  const [showSearchResults, setShowSearchResults] = useState(false);
  const [totalCount, setTotalCount] = React.useState(0);
  const [value, setValue] = useState('');
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [newSelectedProducts, setNewSelectedProducts] = useState([]);
  const [addedProductsTokenId, setAddedProductsTokenId] = useState([]);
  const [filterProducts, setFilterProducts] = useState([]);
  const [filterState, setFilterState] = useState();
  const [selectAllProducts, setSelectAllProducts] = useState(false);
  const [page, setPage] = useState(1);
  const observer = useRef();
  const products = useSelector(state =>
    get(state, 'pagination.products.results', [])
      .map(tokenId => get(state, `entities.products.${tokenId}`))
      .sort((a, b) =>
        // eslint-disable-next-line no-nested-ternary
        a[sortColumn] > b[sortColumn] ? (sortAscending ? 1 : -1) : sortAscending ? -1 : 1,
      ),
  );
  const allProducts = Object.values(useSelector(state => get(state, `entities.products`, [])));
  const partners = useSelector(state => get(state, `entities.partners`, []));
  const searchResults = useSelector(state =>
    Object.values(get(state, 'search.results.products', {})),
  );

  useEffect(() => {
    async function loadProducts() {
      const response = await dispatch(fetchProducts());
      setTotalCount(get(response, `meta.totalCount`, 0));
      setLoading(false);
    }
    loadProducts();
    if (bundleFormData?.componentAllocationPercentage) {
      const existingTokenIds = bundleFormData.componentAllocationPercentage?.length
        ? bundleFormData.componentAllocationPercentage.map(item => item.tokenId)
        : [];
      setSelectedProducts(bundleFormData.componentAllocationPercentage);
      setAddedProductsTokenId(existingTokenIds);
    }

    dispatch(fetchPartners());
  }, [dispatch]);

  const handleColumnClick = (event, column) => {
    setSortAscending(!sortAscending);
    setSortColumn(column.fieldName);
  };

  const handleClear = () => {
    const isAllSelected = every(products, item =>
      selectedProducts.map(val => val.tokenId).includes(item.tokenId),
    );
    setSelectAllProducts(isAllSelected);
    setShowSearchResults(false);
  };

  const handleDropdown = (option, selectedKey) => {
    setFilterState(selectedKey.status);
  };

  const handleSearch = async query => {
    if (!query) {
      const isAllSelected = every(products, item =>
        selectedProducts.map(val => val.tokenId).includes(item.tokenId),
      );
      setSelectAllProducts(isAllSelected);
      setShowSearchResults(false);
      return;
    }
    setShowSearchResults(false);
    setValue(query);

    if (filterState === 'Token ID') {
      setShowSearchResults(true);
      const searchValue = allProducts.filter(p => p._id.toString().includes(query));
      const isAllSelected = every(searchValue, item =>
        selectedProducts.map(val => val.tokenId).includes(item.tokenId),
      );
      setSelectAllProducts(isAllSelected);
      setFilterProducts(searchValue);
    } else if (filterState === 'Product Name') {
      setShowSearchResults(true);
      const searchValue = await dispatch(search(['products'], query));
      const isAllSelected = every(searchValue.payload.products, item =>
        selectedProducts.map(val => val.tokenId).includes(item.tokenId),
      );
      setSelectAllProducts(isAllSelected);
      setFilterProducts(searchValue.payload.products);
    } else {
      setShowSearchResults(true);
      const searchValue = await dispatch(search(['products'], query));
      const isAllSelected = every(searchValue.payload.products, item =>
        selectedProducts.map(val => val.tokenId).includes(item.tokenId),
      );
      setSelectAllProducts(isAllSelected);
      setFilterProducts(searchValue.payload.products);
    }
  };

  const handlePageChange = paginationData => {
    dispatch(
      fetchProducts({
        start: paginationData.currentPageIndex * paginationData.pageLimit,
        count: paginationData.pageLimit,
      }),
    ).then(response => {
      const resProducts = response?.payload?.entities?.products;
      const isAllSelected = resProducts
        ? every(resProducts, item =>
            selectedProducts.map(val => val.tokenId).includes(item.tokenId),
          )
        : false;
      setSelectAllProducts(isAllSelected);
    });
  };

  // load next data when last item is visible
  const lastItemRef = useCallback(
    refElem => {
      if (loading || !refElem) return;
      if (observer.current) observer.current.disconnect();
      observer.current = new IntersectionObserver(entries => {
        if (
          entries[0].isIntersecting &&
          products?.length < totalCount &&
          page < Math.ceil(totalCount / 20)
        ) {
          const data = { currentPageIndex: page, pageLimit: 20 };
          handlePageChange(data);
          setPage(prevPage => prevPage + 1);
        }
      });
      if (refElem) observer.current.observe(refElem);
    },
    [loading, page],
  );

  const onHandleColumnClick = () => {
    const existingProducts = selectedProducts.filter(item =>
      addedProductsTokenId.includes(item.tokenId),
    );
    setNewSelectedProducts([]);
    setSelectedProducts(existingProducts);
    if (selectAllProducts) {
      setSelectAllProducts(false);
    } else {
      const items = showSearchResults ? filterProducts : products;
      const itemsSelected = unionBy(items, existingProducts, 'tokenId');
      setNewSelectedProducts(items);
      setSelectedProducts(itemsSelected);
      setSelectAllProducts(true);
    }
  };

  const onSelectProducts = item => {
    if (
      selectedProducts.length > 0 &&
      selectedProducts.find(product => product.tokenId === item.tokenId)
    ) {
      setSelectedProducts(selectedProducts.filter(product => product.tokenId !== item.tokenId));
      setNewSelectedProducts(
        newSelectedProducts.filter(product => product.tokenId !== item.tokenId),
      );
      // Uncheck the colum header select products if the selected products lengh becomes zero.
      setSelectAllProducts(false);
    } else {
      setSelectedProducts([...selectedProducts, item]);
      setNewSelectedProducts([...newSelectedProducts, item]);
      // Check the colum header select products if the selected products lengh becomes zero.
      if (selectedProducts.length === totalCount - 1) {
        setSelectAllProducts(true);
      }
    }
  };

  const history = useHistory();

  const handleSelectAllButton = () => {
    const items = showSearchResults ? filterProducts : products;
    // getting and combine already added and newly selected
    const addedProducts = selectedProducts.filter(item =>
      addedProductsTokenId.includes(item.tokenId),
    );
    const itemsSelected = unionBy(items, addedProducts, 'tokenId');
    setNewSelectedProducts(items);
    setSelectedProducts(itemsSelected);
    setSelectAllProducts(true);
  };

  const handleDeselectAllButton = () => {
    const alreadySelected = selectedProducts.filter(item =>
      addedProductsTokenId.includes(item.tokenId),
    );
    setNewSelectedProducts([]);
    setSelectedProducts(alreadySelected);
    setSelectAllProducts(false);
  };

  const handleRemoveItem = product => {
    const temp = [...selectedProducts];
    const newItems = [...newSelectedProducts];
    const existingTokenIds = [...addedProductsTokenId];
    remove(existingTokenIds, item => item === product.tokenId);
    remove(newItems, item => item.tokenId === product.tokenId);
    temp.splice(product.value, 1);
    setSelectedProducts(temp);
    setAddedProductsTokenId(existingTokenIds);
    setNewSelectedProducts(newItems);
    if (selectedProducts.length !== products.length) setSelectAllProducts(false);
  };

  const addToBundle = () => {
    if (bundleFormData?.componentAllocationPercentage) {
      const newlySelectedProducts = differenceBy(
        selectedProducts,
        bundleFormData.componentAllocationPercentage,
        'tokenId',
      );

      const removedProducts = differenceBy(
        bundleFormData.componentAllocationPercentage,
        selectedProducts,
        'tokenId',
      );
      if (removedProducts?.length) {
        const removedTokenIds = removedProducts.map(item => item.tokenId);
        remove(bundleFormData.componentAllocationPercentage, item =>
          removedTokenIds.includes(item.tokenId),
        );
      }

      bundleFormData.componentAllocationPercentage =
        bundleFormData?.componentAllocationPercentage.concat(newlySelectedProducts);
    }
    if (newSelectedProducts.length === 0) {
      history.push({ pathname: '/add-bundle/', bundleFormData });
    }

    if (isEdit) {
      history.replace({
        pathname: `/bundles/${bundleId}/edit/`,
        reviseBundleFormData: bundleFormData,
      });
    } else {
      history.push({ pathname: '/add-bundle/', bundleFormData });
    }
  };

  const handleCancel = () => {
    if (isEdit) {
      history.replace({
        pathname: `/bundles/${bundleId}/edit/`,
        reviseBundleFormData: bundleFormData,
      });
    } else {
      history.push({ pathname: '/add-bundle/', bundleFormData });
    }
  };

  const selectMenuProps = {
    items: [
      {
        key: 'selectProductSummary',
        text: 'Select To Add Product to bundle',
        iconProps: { iconName: 'LocationCircle' },
      },
    ],
  };

  const columns = [
    {
      key: 'select',
      name: 'select',
      headerClassName: styles.iconColumnHeaderSelectButton,
      ariaLabel: 'icon',
      iconName: selectAllProducts ? 'SkypeCircleCheck' : '',
      isIconOnly: true,
      fieldName: 'select',
      minWidth: 20,
      maxWidth: 20,
      onColumnClick: onHandleColumnClick,
      onRender: item => {
        const tokenId = get(item, 'tokenId');
        const iconName = find(selectedProducts, { tokenId }) ? 'SkypeCircleCheck' : '';
        const isAlreadySelected = find(
          addedProductsTokenId,
          selectedItem => selectedItem === tokenId,
        );
        return (
          <div>
            <IconButton
              className={clsx(styles.selectButton, isAlreadySelected && styles.disableSelected)}
              menuProps={selectMenuProps}
              iconProps={{ iconName }}
              ariaLabel="Select"
              menuIconProps={{ iconName: '' }}
              onMenuClick={event => {
                event.preventDefault();
                event.stopPropagation();
                onSelectProducts(item);
              }}
              disabled={isAlreadySelected}
            />
          </div>
        );
      },
    },
    {
      key: 'icon',
      name: 'icon',
      headerClassName: styles.iconColumnHeader,
      iconClassName: styles.headerIcon,
      ariaLabel: 'icon',
      iconName: 'Tag',
      isIconOnly: true,
      fieldName: 'icon',
      minWidth: 14,
      maxWidth: 14,
      columnActionsMode: 0,
      onRender: (item, index) => {
        return (
          <div
            ref={products.length === index + 1 ? lastItemRef : null}
            className={styles.iconColumn}
          >
            <FontIcon iconName="Tag" ariaLabel="Tag" className={styles.detailRowIconColor} />
          </div>
        );
      },
    },
    {
      key: 'name',
      name: 'Product Name',
      fieldName: 'name',
      isSorted: sortColumn === 'name',
      isSortedDescending: !sortAscending,
      minWidth: 200,
      maxWidth: 280,
      isResizable: true,
      onColumnClick: handleColumnClick,
      onRender: item => {
        return <div className={styles.productName}>{get(item, 'name')}</div>;
      },
    },
    {
      key: 'tokenId',
      name: 'Token ID',
      fieldName: 'tokenId',
      isSorted: sortColumn === 'tokenId',
      isSortedDescending: !sortAscending,
      minWidth: 130,
      maxWidth: 185,
      isResizable: true,
      onColumnClick: handleColumnClick,
      onRender: item => {
        return <div className={styles.productText}>{`#${item?.tokenId}`}</div>;
      },
    },
    {
      key: 'owner',
      name: 'Owner',
      fieldName: 'owner',
      isSorted: sortColumn === 'owner',
      isSortedDescending: !sortAscending,
      minWidth: 180,
      maxWidth: 300,
      isResizable: true,
      onColumnClick: handleColumnClick,
      onRender: item => {
        return <div className={styles.productText}>{get(partners, `${item?.owner}.name`)}</div>;
      },
    },
    {
      key: 'view',
      name: 'View',
      styles: { root: { visibility: 'hidden' } },
      isResizable: false,
      minWidth: 20,
      maxWidth: 20,
      isPadded: true,
      onRender: item => {
        return (
          <div className={styles.viewButton}>
            <Link to={item.link} target="_blank">
              View
            </Link>
          </div>
        );
      },
    },
  ];

  const updatedProducts = products.map((product, index) => {
    return {
      key: index,
      link: `/products/${product.tokenId}`,
      ...product,
    };
  });

  const columnsWithPartner = [...columns];

  const displayPartnerColumn = Object.values(partners).length > 1;

  const filterOptions = [
    { key: 0, text: 'Token ID', status: 'Token ID' },
    { key: 1, text: 'Product Name', status: 'Product Name' },
    { key: 2, text: 'Product ID', status: 'Product ID' },
  ];

  const stagedColumns = [
    {
      key: 'icon',
      name: 'icon',
      headerClassName: styles.iconColumnHeader,
      iconClassName: styles.headerIcon,
      ariaLabel: 'icon',
      iconName: 'Tag',
      isIconOnly: true,
      fieldName: 'icon',
      minWidth: 14,
      maxWidth: 14,
      columnActionsMode: 0,
      onRender: () => {
        return (
          <div className={styles.iconColumn}>
            <FontIcon iconName="Tag" ariaLabel="Tag" className={styles.detailRowIconColor} />
          </div>
        );
      },
    },
    {
      key: 'name',
      name: 'Product Name',
      fieldName: 'name',
      minWidth: 720,
      maxWidth: 880,
      isResizable: true,
      columnActionsMode: 0,
    },
    {
      key: 'view',
      name: 'View',
      styles: { root: { visibility: 'hidden' } },
      columnActionsMode: 0,
      isResizable: false,
      minWidth: 20,
      maxWidth: 20,
      onRender: item => {
        return (
          <div className={styles.viewButton}>
            <Link to={item.link} target="_blank">
              View
            </Link>
          </div>
        );
      },
    },
    {
      key: 'remove',
      name: 'Remove',
      styles: { root: { visibility: 'hidden' } },
      isResizable: false,
      minWidth: 20,
      maxWidth: 20,
      onRender: (item, i) => {
        return (
          <IconButton
            iconProps={{ iconName: 'Cancel' }}
            className={styles.removeButton}
            ariaLabel="Remove"
            data-testid="remove-item"
            onClick={() => handleRemoveItem(item, i)}
          />
        );
      },
    },
  ];

  return (
    <>
      <PageTitle>
        {isEdit ? (
          <Link to={`/bundles/${bundleId}/edit`} className={styles.breadcrumbs}>
            Revise Bundle
          </Link>
        ) : (
          <Link to="/add-bundle/" className={styles.breadcrumbs}>
            Add Bundle
          </Link>
        )}
        <span className={styles.slash}> / </span>
        Product Search
      </PageTitle>

      <Main>
        <div className={styles.filterAndSearchContainer}>
          <Dropdown
            placeholder="Select"
            options={filterOptions}
            className={styles.filter}
            ariaLabel="filter dropdown"
            styles={{
              title: { backgroundColor: theme.palette.neutralWhite },
              dropdownItem: { fontWeight: 'normal' },
            }}
            onChange={handleDropdown}
          />
          <SearchBar
            placeholder="Search"
            ariaLabel="Search"
            underlined
            onChange={handleSearch} // when you type
            onClear={handleClear} // when you click X
            data-testid="search-input"
            className={styles.search}
          />
        </div>
        {(searchResults.length !== 0 || !showSearchResults || filterProducts.length !== 0) && (
          <div className={styles.selectDeselectButtonsContainer}>
            {newSelectedProducts.length === 0 && (
              <TextButton
                className={styles.textButton}
                onClick={() => handleSelectAllButton()}
                data-testid="select-all"
              >
                Select All
              </TextButton>
            )}
            {newSelectedProducts.length > 0 && (
              <TextButton
                className={styles.textButton}
                onClick={() => handleDeselectAllButton()}
                data-testid="deselect-all"
              >
                Deselect All
              </TextButton>
            )}
          </div>
        )}
        {showSearchResults && (
          <div className={styles.resultsMessage}>
            <Text as="p" block variant="small" className={styles.listHeader}>
              {`${filterProducts.length} Result${
                filterProducts.length !== 1 ? 's' : ''
              } for "${value}"` || `No Results for "${value}"`}
            </Text>
            {filterProducts.length === 0 && (
              <Text as="p" block className={styles.noResultsSentence}>
                Try searching by product name, token ID, or partner name
              </Text>
            )}
          </div>
        )}
        {/* If someone searched but got 0 results, hide the list */}
        {(searchResults.length !== 0 || !showSearchResults || filterProducts.length !== 0) && (
          <Module>
            <ModuleContentList>
              <ProductsList
                columns={displayPartnerColumn ? columnsWithPartner : columns}
                items={showSearchResults ? filterProducts : updatedProducts}
                className={
                  selectedProducts.length ? styles.multipleScrollablePane : styles.scrollablePane
                }
                onHandleColumnClick={onHandleColumnClick}
                selectedProducts={selectedProducts}
                selectAllProducts={selectAllProducts}
                disableRowLink
                shimmered
                enableShimmer={loading}
                noBorderBottom
              />
            </ModuleContentList>
          </Module>
        )}
        {newSelectedProducts.length > 0 && (
          <>
            <div className={styles.countAndButtonsContainer}>
              <Text as="p" block variant="small" className={styles.listHeader}>
                {`${newSelectedProducts.length} Selected Item${
                  newSelectedProducts.length !== 1 ? 's' : ''
                }`}
              </Text>
              <div className={styles.selectDeselectButtonsContainer}>
                <div className={styles.selectDeselectButtonsContainer}>
                  <TextButton
                    className={styles.textButton}
                    onClick={() => handleDeselectAllButton()}
                    data-testid="remove-selected"
                  >
                    Remove All
                  </TextButton>
                </div>
              </div>
            </div>
            <Module>
              <ModuleContentList>
                <ProductsList
                  columns={stagedColumns}
                  items={newSelectedProducts}
                  className={styles.multipleScrollablePane}
                  disableRowLink
                  shimmered
                  enableShimmer={loading}
                  noBorderBottom
                  greyList
                />
              </ModuleContentList>
            </Module>
          </>
        )}
        {(searchResults.length !== 0 || !showSearchResults || filterProducts.length !== 0) && (
          <div className={styles.bundleButtonsContainer}>
            <TextButton className={styles.cancelButton} onClick={handleCancel}>
              Cancel
            </TextButton>
            <PrimaryButton
              disabled={!selectedProducts.length}
              onClick={() => addToBundle()}
              data-testid="add-to-bundle"
            >
              Add to Bundle
            </PrimaryButton>
          </div>
        )}
      </Main>
    </>
  );
};

export default BundleProductList;
